/**
 * Partner Registry
 * ----------------
 *
 * This is where all partner settings are defined, which are not fetched from the backend.
 * - App Title
 * - Favicon
 * - CSS variables (colors) 
 * - Logo
 * - Login Image
 *
 * These are set before login, and used to update the DOM.
 * This is used in the partner/initPartner.ts file.
 * Both the initPartner function and detectPartnerFormHost are used on the Login page - see src/pages/login/LoginPage.tsx
 * When the user is logged in, the partner config is fetched from the backend, in the partner/PartnerContext.tsx file.
 * 
 * #hardcoded : All partners need to be set here.
*/

// UNique key for each partner
export type PartnerKey = "viaads" | "happyads";

// PARTNER_PRESETS type
export type PartnerPreset = {
  key: PartnerKey;
  title: string; // <title> of the app (shown in the browser tab)
  cssVars: Record<string, string>; // CSS variables (colors)
  faviconHref: string; // favicon path
  logoHref?: string;
  loginImgHref?: string;
};

// PARTNER_PRESETS is a record of all partners.
// The above defined key, is used to map the partner to the preset.
export const PARTNER_PRESETS: Record<PartnerKey, PartnerPreset> = {
  viaads: {
    key: "viaads",
    title: "ViaAds Distributor Dashboard",
    cssVars: {
      // Primary brand (violet / purple)
      "--partner-primary": "#5A00FF",
      "--partner-primary-light": "#EDE3FF",
      "--partner-primary-lighter": "#F6F0FF",
      "--partner-primary-dark": "#3A0994",
      "--partner-primary-darker": "#240061",

      // Complementary / accents
      "--partner-secondary": "#EFE6FF",
      "--partner-accent": "#FF6F91",
      "--partner-accent-light": "#FFD6E2",

      // Buttons / states
      "--partner-primary-btn": "#5A00FF",
      "--partner-primary-btn-hover": "#4A00D4",
      "--partner-primary-btn-active": "#3A00A6",
      "--partner-primary-text": "#FFFFFF",

      // Backgrounds / surfaces
      "--partner-bg": "#F8F7FF",
      "--partner-surface": "#FFFFFF",
      "--partner-border": "#E2DAFB",

      // Charts
      "--chart-1": "#5A00FF",
      "--chart-2": "#FDA23A",
      "--chart-3": "#000000",
      "--chart-4": "#FF4096",
      "--chart-5": "#7C3AED",

    },
    faviconHref: "/favicon_viabill.ico",
    logoHref: require("../assets/logos/ViaAdsLogoBlack.png"),
    loginImgHref: require("../assets/images/viaAdsLogin.jpg"),
  },


  happyads: {
    key: "happyads",
    title: "Happy Ads Distributor Dashboard",
    cssVars: {
       // Primary brand (sunny yellow)
      "--partner-primary": "#FCE05E",
      "--partner-primary-light": "#FFF8D6",
      "--partner-primary-lighter": "#FFFCED",
      "--partner-primary-dark": "#C9AD1A",
      "--partner-primary-darker": "#A88B0E",

      // Complementary / accents
      "--partner-secondary": "#FFFCEE",
      "--partner-accent": "#FF69B4",
      "--partner-accent-light": "#FFD1E6",

      // Buttons / states
      "--partner-primary-btn": "#151C21",
      "--partner-primary-btn-hover": "#0E1317",
      "--partner-primary-btn-active": "#000000",
      "--partner-primary-text": "#0E1317",

      // Backgrounds / surfaces
      "--partner-bg": "#FFFEF6",
      "--partner-surface": "#FFFFFF",
      "--partner-border": "#F0E8BE",

      // Charts
      "--chart-1": "#FCE05E",
      "--chart-2": "#FFC3B5",
      "--chart-3": "#151C21",
      "--chart-4": "#FF69B4",
      "--chart-5": "#FFA07A",

    },
    faviconHref: "/favicon_happypay.ico",
    logoHref: require("../assets/logos/HappyAdsLogoBlack.png"),
    loginImgHref: require("../assets/images/happyAdsLogin.jpg"),
  },
};
